import React, { useEffect } from 'react';

import Button from '@/components/Button';
import Svg from '@/components/Svg';
import parseJwt from '@/helpers/common';

import { logError } from '@/helpers/logger/logUtils';

import styles from './SignIn.module.scss';

type AppleLoginButtonPropTypes = {
  clientId: string;
  handleAppleLogin: Function;
  redirectURI: string;
  isCircleButton?: boolean;
  ariaLabel?: string;
};

export const AppleLoginButton = ({
  clientId,
  handleAppleLogin,
  redirectURI,
  isCircleButton,
  ariaLabel = 'Continue with Apple',
}: AppleLoginButtonPropTypes) => {
  useEffect(() => {
    const initAppleAuth = () => {
      window.AppleID?.auth?.init({
        clientId,
        scope: 'name email',
        redirectURI,
        state: 'login',
        usePopup: true,
        responseType: 'code',
        responseMode: 'query',
      });
    };

    if (!window?.AppleID && redirectURI) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
      script.onload = initAppleAuth;
      document.body.appendChild(script);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectURI]);

  const handleSignIn = async () => {
    const { AppleID } = window;
    try {
      const data = await AppleID?.auth?.signIn();
      if (data) {
        const { authorization, user } = data;
        const idToken = parseJwt(authorization.id_token);
        handleAppleLogin(idToken?.sub, user);
      }
    } catch (error: any) {
      logError(`Apple auth failed, ${error?.message}`);
    }
  };

  if (isCircleButton) {
    return (
      <Button className={styles.googleLoginContainer} onClick={handleSignIn} ariaLabel={ariaLabel}>
        <Svg
          glyph="apple"
          ariaLabel="Apple"
          iconId="apple-logo"
          width={32}
          height={32}
          className={styles.appleButtonContainer}
        />
      </Button>
    );
  }

  return (
    <Button className={`${styles.socialButton} ${styles.appleButton} socialButton appleButton`} onClick={handleSignIn}>
      <Svg glyph="apple" iconId="apple-logo" viewBoxX={-3} width={30} height={30} className={styles.apple} />
      <span className={styles.socialButtonText}> Continue with Apple</span>
    </Button>
  );
};
